
// RsaSigner signs an XML provided as string using a signature created with technique described on https://www.xml.com/pub/a/2001/08/08/xmldsig.html#xml-sig
// The technique description doesn't mention one important fact - when calculating the signature from constructed <SignedInfo> tag, the tag MUST include correct namespace, ie. <SignedInfo xmlns="http://www.w3.org/2000/09/xmldsig#">...</SignedInfo>
// However the namespace is finally placed in the <Signature> tag and not in <SignedInfo>.

// Constructor expects PEM formatted private RSA key, pass-phrase for private key decryption and algoritm which should be used for hashing (default is SHA256)

// Private key example:
/*
-----BEGIN RSA PRIVATE KEY-----
Proc-Type: 4,ENCRYPTED
DEK-Info: DES-EDE3-CBC,cb468f0a9101058c

Lx398LRGAXG6O58tWdK2A+/7FaWasUhzAiyJIi4jWF1CU5oV7vSCDoUpDJ3C0HrP
gBMvlMcmUbYY4cR2zcL3UJAHuVkNBH91QNGlTALBelg1ko+EhwJuZSPoHx9+GntI
p/vMeO6YljMmkwJr7uKTMIGjPLXafXh0amEjmdKOIDYTk2vZup0NLnZdSohYZGkA
kD41T8EgCzr56QPB73dsaZA3OLtxGSlDUKtpimb5phOPh/e8x1Swe43bYwUkWybs
FMseDbsHPhyStjhnMXPaj0qR9Gz02Ho2c1QvOObevzLE7b/d3if2BKnEvyiuw2GU
ohBWhzKPiDeFPJmr2wfS0rI5/t93TSDVyLf9Xm+Lt/1YIh1iXxn2SL4lcCknt0LP
ABX4GsN83l5o4XihqNED/zm1xBayR2YlVvw78EzXb/nSYe5ruxchC4D/QK4zVbzo
2v+Ga+30V113yFrFWFAzQUcABaUqhVjEtim8+Z7/WVpIntdO68s/cGyyej+u9P7v
ZzD19DecffOzbmZF+MSkkqT2QuWPRgf3DDSQE3azcQ3yMl7gxRmiS3cHk6YEvaFO
TaJIn0iKh2xRtXfXs7prfg61nDgLs9CRxjKbyGT0Zt04C997s611eDFAZ/74SpfA
a5jylPLxa8JiPkqttjxHWNxl/zaGJpk2LUr2M0egjEAKTcyA96T4c/ETQxfGdDdz
x6isLYj1gVEZYuX4FfBS6EHBH4NrRYhVgV2N6BIHf9t62KI4rLnH5j9UOobf0zZJ
6zmtWwCUyJ/gj3uYZI0NBxp02ZW0dr6+fyyOTphs5iiftLfbMKveX+K0utp0AVtl
FpbCkJKyMirqtMrtgE+EgbNM6n+KlWKxmpM5wBBOpF//Ks+Y3s/Z+6CDd/XSk/ML
Oei2K89YbtQIMLdYKErOfmNBdy8C2giWdg73NsNdSXZ2Afe7SDKQMn4hD5IgThhi
k3GUk9P2Cb3HkYzUbqIhCSwuZC1joo8m1i0F4VTh4xgLAeFpr/TYWwHETI0xKhqN
axSDBTzkzdL39Y5qEZFBznwRBrJkIsF6/b2fmxtduA96v5W34ApesY6agw0OqC8z
KBUqbHR0nmkXR9CZdGR+740j0seCMZtD/M/ZJofbuMUy4JBurEg+hinr/QmKNz8T
r0EEVlrhPg9tMvf2SV1RveJCoH7t0LEcninGdUspeVkNnHkG3484WNd0DJcX7eA7
nu6IKlE9Sr/xhuRi4PQjbzbkUwLR7hfMliv9VlCq95u1U5yo0INZhCeHEU+nNPrq
O15r9au9XOi5Hun5UbIFAw58q3lcCmSX1IJzXymJPFrqwGDnWIxkz9W/PyHeyZhO
fnXdyUbMTZZCKZzALyCBCOyPKlteUumi00mGCINexGPDyUanAEzP79G8ez1Groxt
1n3i1d/eUF8G42J2gAJJmYu0bt1IbnkGdFhamsbXimw9piLUXZ5RZrhISmN1Y4ZE
XzgjTBeS2SGQi5XaA4Xog/P9C0560NgNrukRbb5nQTNpqFosnS30AfG6Fd31eP77
uA+4LmN8E0R1jfWnefO8sKyiGjeCNuIpTxE1u0licJWYU1Y5hgqxIb7yRGN4vVaV
-----END RSA PRIVATE KEY-----
*/

import signer from 'jsrsasign'
import c14n from 'xml-c14n'
import { format } from './StringUtilities'

export const HASH_ALGORITHMS = {
  SHA1: { code: 'sha1', digestMethod: 'http://www.w3.org/2000/09/xmldsig#sha1', signatureMethod: 'http://www.w3.org/2000/09/xmldsig#rsa-sha1' },
  SHA256: { code: 'sha256', digestMethod: 'http://www.w3.org/2001/04/xmlenc#sha256', signatureMethod: 'http://www.w3.org/2001/04/xmldsig-more#rsa-sha256' },
  SHA512: { code: 'sha512', digestMethod: 'http://www.w3.org/2001/04/xmlenc#sha512', signatureMethod: 'http://www.w3.org/2001/04/xmldsig-more#rsa-sha512' },
  RIPEMD160: { code: 'ripemd160', digestMethod: 'http://www.w3.org/2001/04/xmlenc#ripemd160', signatureMethod: 'http://www.w3.org/2001/04/xmldsig-more#rsa-ripemd160' }
}

const SIGNATURE_NAMESPACE = ` xmlns="http://www.w3.org/2000/09/xmldsig#"`
const SIGNED_INFO_TEMPLATE = `<SignedInfo{0}><CanonicalizationMethod Algorithm="http://www.w3.org/TR/2001/REC-xml-c14n-20010315"></CanonicalizationMethod><SignatureMethod Algorithm="{1}"></SignatureMethod><Reference URI=""><Transforms><Transform Algorithm="http://www.w3.org/2000/09/xmldsig#enveloped-signature"></Transform></Transforms><DigestMethod Algorithm="{2}"></DigestMethod><DigestValue>{3}</DigestValue></Reference></SignedInfo>`
const SIGNATURE_TEMPLATE = `<Signature{0}>{1}<SignatureValue>{2}</SignatureValue></Signature>`

export default class RsaSigner {
  constructor (
    privateKeyPemFileContent,
    passphrase,
    hashAlgorithm = HASH_ALGORITHMS.SHA256
  ) {
    this._privateKey = privateKeyPemFileContent
    this._passPhrase = passphrase
    this._algorithm = hashAlgorithm
  }

  signXml (xml) {
    return new Promise((resolve, reject) => {
      this.canonicalize(xml)
        .then(canonicalizedXml => {
          try {
            const signatureTag = this.createSignatureTag(canonicalizedXml)

            // insert signature tag just before closing XML tag
            resolve(this.insertBeforeClosingTag(xml, signatureTag))
          } catch (error) { reject(error) }
        })
        .catch(err => reject(err))
    })
  }

  insertBeforeClosingTag (xml, insertedTag) {
    return xml.replace(/(<\/[^>]+>)\s*$/g, (closingTag) => insertedTag + closingTag)
  }

  createSignatureTag (xml) {
    const messageDigest = this.calculateMessageDigest(xml)
    const signature = this.calculateSignature(messageDigest)
    const signedInfoTag = this.createSignedInfoTag({ messageDigest, namespace: '' })

    return format(
      SIGNATURE_TEMPLATE,
      SIGNATURE_NAMESPACE, // signature namespace is here instead of in SignedInfo tag
      signedInfoTag,
      signature
    )
  }

  // calculates BASE64 encoded hash for provided content (xml as string)
  calculateMessageDigest (xml) {
    const md = new signer.KJUR.crypto.MessageDigest({ 'alg': this._algorithm.code, 'prov': 'cryptojs' })
    md.updateString(xml)
    return signer.hex2b64(md.digest())
  }

  // calculates BASE64 encoded signature from previously calculated digest. The signature is calculated
  // from tag <SignedInfo>...</SignedInfo> containing digest as well as details about used hash algorithm
  // and signature method. SignedInfo tag must have appropriate signature namespace
  calculateSignature (messageDigest) {
    const textToSign = this.createSignedInfoTag({ messageDigest, namespace: SIGNATURE_NAMESPACE })

    const privateKey = new signer.RSAKey()
    privateKey.readPKCS5PrvKeyHex(this.getDecryptedKey())
    return signer.hex2b64(privateKey.sign(textToSign, this._algorithm.code))
  }

  createSignedInfoTag ({ messageDigest, namespace }) {
    return format(
      SIGNED_INFO_TEMPLATE,
      namespace,
      this._algorithm.signatureMethod,
      this._algorithm.digestMethod,
      messageDigest
    )
  }

  getDecryptedKey () {
    try {
      return signer.KEYUTIL.getDecryptedKeyHex(this._privateKey, this._passPhrase)
    } catch (err) {
      throw new Error('Security.InvalidPrivateRsaKey')
    }
  }

  canonicalize (xmlData) {
    return new Promise((resolve, reject) => {
      const doc = new window.DOMParser().parseFromString(xmlData, 'application/xml')
      const canonicaliser = c14n().getAlgorithm('http://www.w3.org/2001/10/xml-exc-c14n#')

      canonicaliser().canonicalise(doc.documentElement, (error, result) => {
        if (error) { reject(error.stack) }
        resolve(result)
      })
    })
  }
}



// WEBPACK FOOTER //
// ./src/utils/RsaSigner.js